<template>
  <div class="bulk-imei-check">
    <div class="d-flex justify-content-end mb-3">
      <b-button
        v-if="hasResult && canExport"
        size="sm"
        variant="success"
        class="btn-lov-export"
        @click="handleExport"
      >
        <i class="fas fa-download mr-1" />
        Xuất Excel
      </b-button>
    </div>

    <!-- Nhập IMEI (Accordion gập dọc) -->
    <div class="lov-card mb-3 input-accordion-card">
      <!-- Tiêu đề / Header Clickable để ẩn/hiện -->
      <div
        class="d-flex align-items-center justify-content-between cursor-pointer card-header-toggle"
        @click="isInputExpanded = !isInputExpanded"
      >
        <div class="d-flex align-items-center">
          <i
            class="fas mr-2 text-primary"
            :class="isInputExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
          />
          <h6 class="lov-card-title mb-0">Danh sách IMEI tra cứu</h6>
          <b-badge
            v-if="imeiCount > 0"
            :variant="overLimit ? 'danger' : 'light'"
            class="ml-2 counter-pill animate-fade-in"
          >
            {{ imeiCount }} / {{ maxImeisLabel }} IMEI
          </b-badge>
        </div>
        <div class="d-flex align-items-center">
          <small class="text-muted mr-2">
            {{ isInputExpanded ? 'Nhấn để thu gọn' : 'Nhấn để mở rộng' }}
          </small>
        </div>
      </div>

      <!-- Phần thân collapse chứa ô nhập liệu và các công cụ -->
      <b-collapse v-model="isInputExpanded" class="mt-3">
        <hr class="my-3" style="border-top: 1px dashed #e5e7eb;">
        <b-row>
          <!-- Cột bên trái: Ô nhập liệu textarea -->
          <b-col cols="12" md="8">
            <b-form-textarea
              v-model="rawInput"
              class="imei-textarea"
              rows="6"
              max-rows="8"
              placeholder="Nhập hoặc dán danh sách IMEI tại đây. Phân tách bằng khoảng trắng, dấu phẩy hoặc xuống dòng..."
            />
          </b-col>
          
          <!-- Cột bên phải: Các nút công cụ và nút Tìm kiếm -->
          <b-col cols="12" md="4" class="d-flex flex-column justify-content-between mt-3 mt-md-0">
            <div>
              <p class="input-hint mb-3 text-muted" style="line-height: 1.4;">
                Hệ thống hỗ trợ nhập liệu trực tiếp hoặc import file Excel/CSV. Giới hạn tối đa <strong>{{ maxImeisLabel }}</strong> IMEI cho mỗi lần tra cứu.
              </p>
              <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-2 mb-1"
                  @click="triggerFilePicker"
                >
                  <i class="fas fa-file-upload mr-1" />
                  Import Excel
                </b-button>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-2 mb-1"
                  @click="downloadTemplate"
                >
                  <i class="fas fa-file-download mr-1" />
                  Mẫu
                </b-button>
                <b-button
                  variant="link"
                  size="sm"
                  class="text-danger p-0 ml-auto mb-1"
                  :disabled="!rawInput"
                  @click="clearInput"
                >
                  <i class="fas fa-eraser mr-1" />
                  Xóa sạch
                </b-button>
                <input
                  ref="fileInput"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  class="d-none"
                  @change="onFileChange"
                />
              </div>
            </div>
            
            <b-button
              block
              variant="primary"
              class="mt-auto font-weight-bolder"
              :disabled="!imeiCount || loading"
              @click="lookup"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-1"
              />
              <i
                v-else
                class="fas fa-search mr-1"
              />
              Tra cứu ngay
            </b-button>
          </b-col>
        </b-row>
      </b-collapse>
    </div>

    <!-- Thanh trạng thái tiến trình (Progress Bar) khi đang load -->
    <div
      v-if="loading"
      class="lov-card mb-3 animate-fade-in"
    >
      <div class="d-flex align-items-center justify-content-between mb-2">
        <span class="font-weight-bold text-dark" style="font-size: 14px;">
          <b-spinner small variant="primary" class="mr-2" />
          Đang tra cứu và nạp dữ liệu IMEI... ({{ processedCount }}/{{ totalToProcess }})
        </span>
        <span class="font-weight-bold text-primary" style="font-size: 15px;">{{ Math.round(progress) }}%</span>
      </div>
      <b-progress :value="progress" max="100" animated variant="primary" style="height: 10px; border-radius: 6px;" />
    </div>

    <!-- Màn hình trống khi chưa tìm kiếm -->
    <div
      v-if="!hasResult && !loading"
      class="lov-card empty-state"
    >
      <i class="fas fa-search empty-icon" />
      <p class="empty-text mb-0">
        Dán danh sách IMEI vào ô bên trên rồi nhấn <strong>Tra cứu ngay</strong> để xem thông tin vòng đời chi tiết.
      </p>
    </div>

    <!-- Kết quả Tra cứu -->
    <template v-if="hasResult">
      <b-alert
        v-if="truncated"
        variant="warning"
        show
        class="mb-3"
      >
        Đã nhận {{ requestedCount }} IMEI, chỉ tra cứu {{ maxImeis }} IMEI đầu tiên.
      </b-alert>

      <b-row class="mb-3">
        <b-col
          v-for="card in summaryCards"
          :key="card.label"
          cols="6"
          lg="3"
          class="mb-2 mb-lg-0"
        >
          <div class="lov-card kpi-card">
            <small class="kpi-label">{{ card.label }}</small>
            <div
              class="kpi-value"
              :class="card.toneClass"
            >
              {{ card.value }}
            </div>
          </div>
        </b-col>
      </b-row>

      <div
        v-if="missingImeis.length"
        class="lov-card missing-card mb-3"
      >
        <h6 class="lov-card-title mb-2">
          Không tồn tại trong hệ thống — {{ missingImeis.length }} IMEI
        </h6>
        <div class="missing-chips">
          <b-badge
            v-for="imei in missingImeis"
            :key="imei"
            variant="light"
            class="missing-chip"
          >
            <i class="fas fa-times-circle text-danger mr-1" />
            {{ imei }}
          </b-badge>
        </div>
      </div>

      <div class="lov-card">
        <h6 class="lov-card-title mb-3">Kết quả tra cứu — {{ foundRows.length }} dòng</h6>
        <div class="table-responsive lov-table-wrap">
          <b-table
            :items="foundRows"
            :fields="resultFields"
            small
            hover
            show-empty
            empty-text="Không có dòng kết quả"
            class="lov-table mb-0"
          >
            <template #cell(stt)="data">
              {{ data.index + 1 }}
            </template>
            <template #cell(tenSp)="data">
              <div class="product-meta">
                <div class="product-name">
                  {{ data.item.tenSp || data.item.tenHang || '—' }}
                </div>
                <div
                  v-if="data.item.maHang"
                  class="product-code"
                >
                  {{ data.item.maHang }}
                </div>
              </div>
            </template>
            <template #cell(trangThai)="data">
              <b-badge
                v-if="data.item.trangThai"
                :variant="statusVariant(data.item.trangThai)"
                class="status-badge"
              >
                {{ data.item.trangThai }}
              </b-badge>
              <span v-else>—</span>
            </template>
            <template #cell(ngayNhap)="data">
              {{ formatDate(data.item.ngayNhap) }}
            </template>
            <template #cell(ngayBan)="data">
              {{ formatDate(data.item.ngayBan) }}
            </template>
            <template #cell(ncc)="data">
              <div class="ncc-cell">
                <div>{{ data.item.ncc || '—' }}</div>
                <small
                  v-if="data.item.maNccPhu"
                  class="text-muted"
                >{{ data.item.maNccPhu }}</small>
              </div>
            </template>
            <template #cell(khoTon)="data">
              {{ data.item.khoTon || '—' }}
            </template>
            <template #cell(khoBan)="data">
              {{ data.item.khoBan || '—' }}
            </template>
            <template #cell(giaMua)="data">
              <span
                v-if="!canViewOriginalPrice"
                class="text-muted"
                title="Cần quyền PRODUCT_ORIGINAL_PRICE_VIEW"
              >—</span>
              <span
                v-else
                class="cell-money cell-money--muted"
              >{{ formatMoney(data.item.giaMua) }}</span>
            </template>
            <template #cell(giaBan)="data">
              <span class="cell-money cell-money--bold">{{ formatMoney(data.item.giaBan) }}</span>
            </template>
          </b-table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import xlsx from 'xlsx';
import moment from 'moment';

import permissionMixin from '@/view/mixins/permission.mixin';
import { convertPrice, makeToastFaile, makeToastSuccess } from '@/utils/common';
import { retryWithExponentialBackoff } from '@/utils/retry';
import SalesReportsService from '../services/SalesReportsService';
import { BULK_IMEI_MAX } from '../constants';

const splitImeis = (raw) =>
  String(raw || '')
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);

export default {
  name: 'SalesReportsBulkImeiCheck',
  mixins: [permissionMixin],
  data() {
    return {
      rawInput: '',
      loading: false,
      hasResult: false,
      rows: [],
      requestedCount: 0,
      truncated: false,
      maxImeis: BULK_IMEI_MAX,
      lastSentImeis: [],
      progress: 0,
      processedCount: 0,
      totalToProcess: 0,
      isInputExpanded: true,
    };
  },
  computed: {
    maxImeisLabel() {
      return BULK_IMEI_MAX.toLocaleString('vi-VN');
    },
    parsedImeis() {
      return splitImeis(this.rawInput);
    },
    imeiCount() {
      return this.parsedImeis.length;
    },
    overLimit() {
      return this.imeiCount > BULK_IMEI_MAX;
    },
    canExport() {
      return this.checkPermission('SALES_REPORT_EXPORT');
    },
    canViewOriginalPrice() {
      return this.checkPermission('PRODUCT_ORIGINAL_PRICE_VIEW');
    },
    foundRows() {
      return this.rows.filter((row) => !row.notFound);
    },
    soldCount() {
      return this.foundRows.filter((row) => row.ngayBan || row.phieuBan).length;
    },
    missingImeis() {
      return this.rows.filter((row) => row.notFound).map((row) => row.imei);
    },
    summaryCards() {
      return [
        { label: 'IMEI gửi đi', value: String(this.requestedCount), toneClass: '' },
        { label: 'Tìm thấy', value: String(this.foundRows.length), toneClass: 'kpi-value--primary' },
        {
          label: 'Không tồn tại',
          value: String(this.missingImeis.length),
          toneClass: 'kpi-value--danger',
        },
        { label: 'Đã bán', value: String(this.soldCount), toneClass: 'kpi-value--success' },
      ];
    },
    resultFields() {
      return [
        { key: 'stt', label: 'STT' },
        { key: 'imei', label: 'IMEI' },
        { key: 'tenSp', label: 'Sản phẩm' },
        { key: 'trangThai', label: 'Trạng thái' },
        { key: 'ngayNhap', label: 'Ngày nhập' },
        { key: 'phieuNhap', label: 'Phiếu nhập' },
        {
          key: 'ncc',
          label: 'NCC',
          thStyle: { width: '220px' },
        },
        { key: 'giaMua', label: 'Giá mua (sau VAT)', class: 'text-right' },
        { key: 'khoTon', label: 'Kho tồn' },
        { key: 'khoBan', label: 'Kho bán' },
        { key: 'ngayBan', label: 'Ngày bán' },
        { key: 'phieuBan', label: 'Phiếu bán' },
        { key: 'khachHang', label: 'Khách hàng' },
        { key: 'giaBan', label: 'Giá bán (sau VAT)', class: 'text-right' },
      ];
    },
  },
  methods: {
    statusVariant(status) {
      const normalized = String(status || '').toLowerCase();
      if (normalized.includes('bán')) {
        return 'success';
      }
      if (normalized.includes('tồn')) {
        return 'primary';
      }
      return 'secondary';
    },
    formatDate(value) {
      if (!value) {
        return '—';
      }
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      const num = Number(value);
      return num < 0 ? `-${convertPrice(Math.abs(num))}` : convertPrice(num);
    },
    clearInput() {
      this.rawInput = '';
    },
    triggerFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }
      const valid = /\.(xlsx|xls|csv)$/i.test(file.name);
      if (!valid) {
        makeToastFaile('Vui lòng tải lên file .xlsx, .xls hoặc .csv');
        event.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const workbook = xlsx.read(ev.target.result, { type: 'binary' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const matrix = xlsx.utils.sheet_to_json(sheet, {
            header: 1,
            blankRows: false,
            defval: '',
          });
          const imeis = [];
          matrix.forEach((line) => {
            (line || []).forEach((cell) => {
              const value = String(cell).trim();
              if (value && !/^imei$/i.test(value)) {
                imeis.push(value);
              }
            });
          });
          if (!imeis.length) {
            makeToastFaile('Không tìm thấy IMEI trong file.');
            return;
          }
          const existing = this.rawInput ? `${this.rawInput}\n` : '';
          this.rawInput = existing + imeis.join('\n');
          makeToastSuccess(`Đã nạp ${imeis.length} IMEI từ file.`);
        } catch (e) {
          makeToastFaile('Không đọc được file Excel.');
        }
      };
      reader.readAsBinaryString(file);
      event.target.value = '';
    },
    downloadTemplate() {
      const wb = xlsx.utils.book_new();
      const ws = xlsx.utils.aoa_to_sheet([
        ['IMEI'],
        ['865292084590709'],
        ['350833931281414'],
      ]);
      xlsx.utils.book_append_sheet(wb, ws, 'IMEI');
      xlsx.writeFile(wb, 'Mau_check_imei.xlsx');
    },
    async lookup() {
      if (!this.imeiCount) {
        return;
      }
      const sent = this.parsedImeis.slice(0, BULK_IMEI_MAX);

      // Reset toàn bộ state trước mỗi lần tra cứu mới
      this.rows = [];
      this.hasResult = false;
      this.loading = true;
      this.progress = 0;
      this.processedCount = 0;
      this.totalToProcess = sent.length;
      this.isInputExpanded = false;
      this.requestedCount = 0;
      this.truncated = false;
      this.lastSentImeis = [];

      const CLIENT_CHUNK = 20;
      try {
        for (let i = 0; i < sent.length; i += CLIENT_CHUNK) {
          const part = sent.slice(i, i + CLIENT_CHUNK);
          let chunkFailed = false;
          try {
            const data = await retryWithExponentialBackoff({
              fn: () => SalesReportsService.bulkCheckImei(part),
              retries: 5,
              baseDelayMs: 1000,
            });
            if (data?.rows?.length) {
              // Ghi stt liên tục dựa theo vị trí hiện tại trong this.rows
              const offset = this.rows.length;
              const mapped = data.rows.map((row, idx) => ({ ...row, stt: offset + idx + 1 }));
              this.rows.push(...mapped);

              // Hiển thị bảng ngay sau chunk đầu tiên có dữ liệu
              if (!this.hasResult) {
                this.hasResult = true;
                this.requestedCount = this.parsedImeis.length;
                this.maxImeis = BULK_IMEI_MAX;
                this.truncated = this.parsedImeis.length > BULK_IMEI_MAX;
              }
            }
          } catch (chunkError) {
            console.error(`Chunk [${i + 1}–${i + part.length}] failed after 3 retries:`, chunkError);
            makeToastFaile(
              `Không thể tra cứu nhóm IMEI từ ${i + 1} đến ${i + part.length} sau 3 lần thử. Kết quả tới đây sẽ được giữ lại.`
            );
            chunkFailed = true;
          }
          if (chunkFailed) break;
          this.processedCount = Math.min(sent.length, i + part.length);
          this.progress = (this.processedCount / sent.length) * 100;
          // Cập nhật dần để export chủ động được phần đã load
          this.lastSentImeis = sent.slice(0, this.processedCount);
        }

        if (this.rows.length > 0) {
          makeToastSuccess(`Đã tra cứu ${this.processedCount} IMEI`);
        }
      } catch (error) {
        makeToastFaile(error.message || 'Lỗi tra cứu IMEI');
      } finally {
        this.loading = false;
      }
    },
    handleExport() {
      if (!this.hasResult || !this.rows.length) {
        return;
      }
      if (!this.canExport) {
        makeToastFaile('Bạn chưa có quyền xuất Excel (SALES_REPORT_EXPORT).');
        return;
      }

      const fmtMoney = (value) => {
        if (value === null || value === undefined || value === '') return '';
        return Number(value);
      };

      // Build dữ liệu xuất — mirror cột của BE header.js
      const exportData = this.rows.map((row) => ({
        'TT': row.stt ?? '',
        'IMEI': row.imei ?? '',
        'Mã hàng': row.maHang ?? '',
        'Tên sản phẩm': row.tenSp ?? '',
        'Ngày nhập': row.ngayNhap ? this.formatDate(row.ngayNhap) : '',
        'Phiếu nhập': row.phieuNhap ?? '',
        'NCC': row.ncc ?? '',
        'Mã phụ NCC': row.maNccPhu ?? '',
        'Trạng thái': row.trangThai ?? '',
        'Kho tồn': row.khoTon ?? '',
        'Kho bán': row.khoBan ?? '',
        'Ngày bán': row.ngayBan ? this.formatDate(row.ngayBan) : '',
        'Phiếu bán': row.phieuBan ?? '',
        'Khách hàng': row.khachHang ?? '',
        'P. Chuyển': row.pChuyen ?? '',
        'Giá mua': fmtMoney(row.giaMua),
        'Giá bán': fmtMoney(row.giaBan),
        'Dealsheet 1': fmtMoney(row.dealsheet1),
        'Dealsheet 2': fmtMoney(row.dealsheet2),
        'Dealsheet 3': fmtMoney(row.dealsheet3),
      }));

      const wb = xlsx.utils.book_new();
      const ws = xlsx.utils.json_to_sheet(exportData);

      // Thiết lập độ rộng cột tự động theo nội dung
      const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
        wch: Math.max(
          key.length + 2,
          ...exportData.map((row) => String(row[key] ?? '').length),
        ),
      }));
      ws['!cols'] = colWidths;

      xlsx.utils.book_append_sheet(wb, ws, 'Check IMEI hàng loạt');

      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      xlsx.writeFile(wb, `check_imei_hang_loat_${dateStr}.xlsx`);
    },
  },
};
</script>

<style scoped>
.lov-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}
.lov-card-title {
  font-weight: 600;
  color: #111827;
}
.input-hint {
  font-size: 12px;
  color: #6b7280;
}
.clear-btn {
  font-size: 12px;
  white-space: nowrap;
}
.imei-textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  border-radius: 8px;
}
.counter-pill {
  font-size: 12px;
  font-weight: 600;
}
.empty-state {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.empty-icon {
  font-size: 28px;
  color: #d1d5db;
  margin-bottom: 12px;
}
.empty-text {
  color: #6b7280;
  font-size: 14px;
  max-width: 360px;
}
.kpi-card {
  height: 100%;
}
.kpi-label {
  color: #6b7280;
  font-size: 12px;
  display: block;
  margin-bottom: 6px;
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.kpi-value--primary {
  color: #2563eb;
}
.kpi-value--success {
  color: #16a34a;
}
.kpi-value--danger {
  color: #dc2626;
}
.missing-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.missing-chip {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 6px 10px;
}
.btn-lov-export {
  border-radius: 8px;
  font-weight: 500;
  background: #16a34a;
  border-color: #16a34a;
}
.lov-table-wrap {
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;
}
.lov-table ::v-deep thead th {
  background: #f3f4f6 !important;
  color: #374151;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
}
.lov-table ::v-deep tbody td {
  vertical-align: middle;
  font-size: 13px;
}
.product-name {
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}
.product-code {
  font-size: 11px;
  color: #9ca3af;
}
.ncc-cell {
  line-height: 1.3;
  min-width: 200px;
  white-space: normal;
  word-break: break-word;
}
.lov-table ::v-deep th.col-ncc,
.lov-table ::v-deep td.col-ncc {
  min-width: 220px;
}
.status-badge {
  font-size: 11px;
  font-weight: 600;
}
.cell-money {
  font-variant-numeric: tabular-nums;
}
.cell-money--bold {
  font-weight: 700;
  color: #111827;
}
.cell-money--muted {
  color: #6b7280;
}
.cursor-pointer {
  cursor: pointer;
}
.card-header-toggle {
  user-select: none;
  transition: background-color 0.15s ease;
}
.card-header-toggle:hover .lov-card-title {
  color: #2563eb !important;
}
.animate-fade-in {
  animation: fadeIn 0.25s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
